
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Book, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle, Brain, 
  Bot, Users, GitMerge, Info 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AdvancedFeaturesContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const AdvancedFeaturesContent: React.FC<AdvancedFeaturesContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "ai-job-matching") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Job Matching</h1>
          <p className="text-gray-500 mt-2">
            Learn how to leverage our advanced AI job matching system to find perfect opportunities.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: May 28, 2023</span>
            <Badge variant="outline" className="ml-2">Premium Feature</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="how-it-works">How AI Job Matching Works</h2>
          <p>
            The AI Job Matching system uses advanced machine learning algorithms to analyze your profile, skills, portfolio, 
            and preferences to match you with relevant job opportunities. The system continuously learns from your interactions 
            to improve match quality over time.
          </p>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-6">
            <h3 className="text-base font-medium mb-2">Key Matching Factors</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 list-none pl-0">
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Skills alignment</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Experience level</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Portfolio quality</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Project history</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Client feedback</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Availability</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Rate compatibility</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Work preferences</li>
            </ul>
          </div>

          <h2 id="match-score">Understanding Your Match Score</h2>
          <p>
            Each job opportunity is assigned a match score from 0-100%, indicating how well the position aligns with your profile:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-1">90-100%: Perfect Match</h3>
                <p className="text-sm text-gray-500">These opportunities align exceptionally well with your profile and preferences.</p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-1">70-89%: Strong Match</h3>
                <p className="text-sm text-gray-500">Good alignment with your skills, though may have some differences from ideal preferences.</p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-1">Below 70%: Potential Match</h3>
                <p className="text-sm text-gray-500">May require skill development or additional considerations.</p>
              </CardContent>
            </Card>
          </div>

          <h2 id="premium-features">Premium AI Matching Features</h2>
          <p>
            Premium account holders get access to enhanced AI matching capabilities:
          </p>

          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 my-6">
            <h3 className="text-base font-medium text-purple-800 mb-2">Premium Features</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-4 w-4 text-purple-600 mt-1 mr-2 flex-shrink-0" />
                <div>
                  <strong>Predictive Matching</strong> - AI predicts which jobs you're most likely to win based on historical data.
                </div>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 text-purple-600 mt-1 mr-2 flex-shrink-0" />
                <div>
                  <strong>Skill Gap Analysis</strong> - Identifies skills to develop to qualify for more opportunities.
                </div>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 text-purple-600 mt-1 mr-2 flex-shrink-0" />
                <div>
                  <strong>Early Access</strong> - Get notified about matching jobs before they're publicly posted.
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["ai-job-matching"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("ai-job-matching", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("ai-job-matching", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("ai-job-matching") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("ai-job-matching")}
            >
              {bookmarkedArticles.includes("ai-job-matching") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "ai-agents") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Agents</h1>
          <p className="text-gray-500 mt-2">
            Discover how AI agents can streamline your workflow and boost productivity.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 3, 2023</span>
            <Badge variant="outline" className="ml-2 bg-violet-50 text-violet-700 border-violet-200">Premium</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="what-are-ai-agents">What are AI Agents?</h2>
          <p>
            AI Agents are intelligent virtual assistants designed to help freelancers, agencies, and clients automate tasks, 
            analyze data, and make better decisions. These specialized tools leverage advanced machine learning to perform 
            specific functions related to project management, client communication, skill development, and more.
          </p>

          <h2 id="available-agents">Available AI Agents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card className="border-l-4 border-l-violet-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-violet-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Project Analyzer</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Evaluates project requirements, estimates effort, identifies potential risks, and suggests optimal team composition.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-indigo-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Brain className="h-5 w-5 text-indigo-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Proposal Assistant</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Generates tailored proposals, analyzes win rates, and provides suggestions to improve conversion.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Client Relationship Manager</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Tracks client interactions, suggests engagement opportunities, and provides insights on client preferences.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <GitMerge className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Team Optimizer</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Analyzes team performance, identifies skill gaps, and suggests optimal work distribution.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              AI Agent Usage Limits
            </h3>
            <p className="text-blue-700 text-sm">
              Premium users can access all AI agents with 50 interactions per month. Enterprise plans include unlimited agent usage.
              Free accounts can try each agent once as a trial.
            </p>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["ai-agents"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("ai-agents", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("ai-agents", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("ai-agents") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("ai-agents")}
            >
              {bookmarkedArticles.includes("ai-agents") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "team-builder") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team Builder</h1>
          <p className="text-gray-500 mt-2">
            Learn how to use our powerful Team Builder tool to assemble the perfect team for any project.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: June 12, 2023</span>
            <Badge variant="outline" className="ml-2">Enterprise Feature</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="team-builder-overview">Team Builder Overview</h2>
          <p>
            The Team Builder feature allows agencies and enterprise clients to quickly assemble optimal project teams
            based on skills, availability, past collaboration success, and other key factors. This data-driven approach
            ensures you have the right talent for every project requirement.
          </p>

          <Tabs defaultValue="agencies" className="w-full my-6">
            <TabsList className="w-full">
              <TabsTrigger value="agencies" className="flex-1">For Agencies</TabsTrigger>
              <TabsTrigger value="enterprises" className="flex-1">For Enterprises</TabsTrigger>
              <TabsTrigger value="freelancers" className="flex-1">For Freelancers</TabsTrigger>
            </TabsList>
            <TabsContent value="agencies" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Agency Benefits</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Create talent pools from your network of freelancers</li>
                <li>Quickly assemble teams based on project requirements</li>
                <li>Track team performance and optimize future compositions</li>
                <li>Manage resource allocation across multiple projects</li>
                <li>Generate team proposals for client projects</li>
              </ul>
            </TabsContent>
            <TabsContent value="enterprises" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Enterprise Benefits</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access a verified network of pre-vetted professionals</li>
                <li>Build teams that blend internal staff with external talent</li>
                <li>Define specific skills and experience requirements</li>
                <li>Track budget allocation across team members</li>
                <li>Integrate with existing enterprise workforce systems</li>
              </ul>
            </TabsContent>
            <TabsContent value="freelancers" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Freelancer Benefits</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Increased visibility to agencies and enterprise clients</li>
                <li>More opportunities for team-based projects</li>
                <li>Discovery based on your unique skill combinations</li>
                <li>Build relationships with complementary professionals</li>
                <li>Access to larger, higher-value projects</li>
              </ul>
            </TabsContent>
          </Tabs>

          <h2 id="key-features">Key Features</h2>
          <div className="my-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-base font-medium mb-4">Team Builder Core Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
              <div className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2" />
                <div>
                  <h4 className="font-medium text-sm">AI-Powered Recommendations</h4>
                  <p className="text-xs text-gray-500">Smart suggestions based on project requirements and past performance</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2" />
                <div>
                  <h4 className="font-medium text-sm">Skill Gap Analysis</h4>
                  <p className="text-xs text-gray-500">Identify missing skills in your proposed team</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2" />
                <div>
                  <h4 className="font-medium text-sm">Team Templates</h4>
                  <p className="text-xs text-gray-500">Save successful team compositions for future use</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2" />
                <div>
                  <h4 className="font-medium text-sm">Budget Calculation</h4>
                  <p className="text-xs text-gray-500">Estimate team costs based on member rates</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2" />
                <div>
                  <h4 className="font-medium text-sm">Availability Tracking</h4>
                  <p className="text-xs text-gray-500">See when team members are available for new projects</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2" />
                <div>
                  <h4 className="font-medium text-sm">Team Collaboration Analysis</h4>
                  <p className="text-xs text-gray-500">Insights on how well team members work together</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["team-builder"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("team-builder", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("team-builder", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("team-builder") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("team-builder")}
            >
              {bookmarkedArticles.includes("team-builder") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
